// The theatre diary for one day, as a doctor is allowed to see it.
//
// A doctor asking for an operation was choosing a date and a time blind — the
// OT schedule screen sits behind a permission a surgeon in clinic does not
// hold — so the "preference" they typed was a guess, and OT spent the day
// renegotiating it. This is the same diary, read only, beside the fields being
// filled in, so the guess becomes a choice.
//
// OTHER SURGEONS' PATIENTS ARE NOT HERE. The server sends theatre, slot and
// operating surgeon for a case that is not the caller's, and no patient name at
// all; this component could not display one if it tried. Redaction that happens
// in the browser is not redaction.
//
// Deliberately NOT a slot picker. The doctor still types the time they want —
// the diary informs that choice, it does not make it, because a free hour on
// this grid may be unusable for reasons a doctor cannot see (kit, staffing, a
// list that always overruns). OT still books the real time.

import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import moment from "moment";

import { fetchDoctorSchedule } from "../../ot/api/otApi";
import { formatIstTime } from "../../../utils/istDateTime";

const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const MINE = "#0F4C5C";
const OTHERS = "#94A3B8";

// The window a theatre list realistically occupies. Cases outside it still
// render — they are clamped rather than hidden, because a case that vanished
// off the top of the grid is worse than one squashed against it.
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;
const HOURS = DAY_END_HOUR - DAY_START_HOUR;
const ROW_PX = 34;

/** Fraction down the grid, clamped to the visible window. */
function offsetOf(iso) {
  const m = moment(iso);
  const hours = m.hour() + m.minute() / 60;
  return Math.min(Math.max((hours - DAY_START_HOUR) / HOURS, 0), 1);
}

function Block({ booking, top, height }) {
  const mine = booking.mine;
  return (
    <Box
      title={
        mine
          ? `${booking.patient_name || "Your case"} — ${
              booking.procedure_name || "procedure not named"
            }`
          : `Booked by ${booking.surgeon_name || "another surgeon"}`
      }
      sx={{
        position: "absolute",
        left: 3,
        right: 3,
        top: `${top * 100}%`,
        height: `${Math.max(height * 100, 3)}%`,
        minHeight: 18,
        borderRadius: "4px",
        px: 0.75,
        py: 0.25,
        overflow: "hidden",
        backgroundColor: mine ? MINE : "#F1F5F9",
        color: mine ? "#FFFFFF" : "#334155",
        border: `1px solid ${mine ? MINE : BORDER}`,
        fontSize: 10.5,
        lineHeight: 1.25,
      }}
    >
      <Box sx={{ fontWeight: 700 }}>
        {formatIstTime(booking.start_datetime)}
        {booking.duration_min ? ` · ${booking.duration_min}m` : ""}
      </Box>
      <Box sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
        {mine
          ? booking.patient_name || "Your case"
          : booking.surgeon_name || "Booked"}
      </Box>
    </Box>
  );
}

export default function OtDaySchedule({ date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError("");
    try {
      setData(await fetchDoctorSchedule(date));
    } catch (err) {
      // The diary is an aid, not a prerequisite. If it cannot be read the
      // doctor can still raise the request, so this says so quietly rather
      // than blocking the form.
      setError(err?.message || "Could not load the theatre diary");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const rooms = data?.rooms || [];
  const bookings = data?.bookings || [];
  const blocks = data?.blocks || [];

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: "8px", p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          Theatre on {moment(date, "YYYY-MM-DD").format("ddd D MMM")}
        </Typography>
        <Typography sx={{ fontSize: 11, color: MUTED }}>view only</Typography>
        {loading && <CircularProgress size={12} />}
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 12, color: MUTED }}>{error}</Typography>
      ) : rooms.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          {loading ? "Loading…" : "No theatres are configured."}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* The hour rail. */}
            <Box sx={{ width: 34, position: "relative", height: HOURS * ROW_PX }}>
              {Array.from({ length: HOURS + 1 }, (_, i) => (
                <Typography
                  key={i}
                  sx={{
                    position: "absolute",
                    top: `${(i / HOURS) * 100}%`,
                    fontSize: 9.5,
                    color: MUTED,
                    transform: "translateY(-50%)",
                  }}
                >
                  {String(DAY_START_HOUR + i).padStart(2, "0")}:00
                </Typography>
              ))}
            </Box>

            {rooms.map((room) => {
              const mine = bookings.filter((b) => b.operating_room_id === room.id);
              const unavailable = blocks.filter((b) => b.operating_room_id === room.id);
              return (
                <Box key={room.id} sx={{ flex: 1, minWidth: 74 }}>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      textAlign: "center",
                      mb: 0.25,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {room.name}
                  </Typography>
                  <Box
                    sx={{
                      position: "relative",
                      height: HOURS * ROW_PX,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "4px",
                      backgroundImage: `repeating-linear-gradient(${BORDER} 0 1px, transparent 1px ${ROW_PX}px)`,
                    }}
                  >
                    {unavailable.map((blk, i) => {
                      const top = offsetOf(blk.start_datetime);
                      return (
                        <Box
                          key={`blk-${i}`}
                          title="Theatre unavailable"
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: `${top * 100}%`,
                            height: `${Math.max(
                              (offsetOf(blk.end_datetime) - top) * 100,
                              2,
                            )}%`,
                            backgroundImage:
                              "repeating-linear-gradient(45deg, #E2E8F0 0 4px, transparent 4px 8px)",
                          }}
                        />
                      );
                    })}
                    {mine.map((b) => {
                      const top = offsetOf(b.start_datetime);
                      return (
                        <Block
                          key={b.booking_id}
                          booking={b}
                          top={top}
                          height={offsetOf(b.end_datetime) - top}
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, mt: 1, alignItems: "center", flexWrap: "wrap" }}>
            <LegendSwatch colour={MINE} label="Yours" />
            <LegendSwatch colour={OTHERS} label="Another surgeon" />
            <Typography sx={{ fontSize: 10.5, color: MUTED }}>
              Other surgeons' patients are not shown.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}

function LegendSwatch({ colour, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: "2px", backgroundColor: colour }} />
      <Typography sx={{ fontSize: 10.5, color: MUTED }}>{label}</Typography>
    </Box>
  );
}
